import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Building2, ChevronLeft, ChevronRight, Search } from "lucide-react";
import { Link } from "react-router-dom";

import { SKU } from "@/entities/pricing/model/priceBook";
import { usePersona } from "@/shared/brand/persona";
import { usePriceBook } from "@/entities/pricing/model/usePriceBook";
import { toast } from "@/shared/hooks/use-toast";
import { listCityGroups, type CityGroup } from "@/shared/lib/amap/districtService";
import { errorText, errorToast } from "@/shared/lib/apiErrors";
import { Button } from "@/shared/ui/button";
import { Checkbox } from "@/shared/ui/checkbox";
import { CityCombobox } from "@/shared/ui/CityCombobox";
import { Input } from "@/shared/ui/input";
import { Skeleton } from "@/shared/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/ui/table";

import {
  acqService,
  type AcqEntCompany,
  type AcqEntOptions,
  type AcqEntSearchResult,
  type AcqEntSuggestion,
} from "../api/acqService";
import { PROVINCES, areaCodeOf, matchRegion, type AreaPick } from "../model/entArea";
import { useTenantProducts } from "../model/useTenantProducts";
import { AcqShell } from "./AcqShell";
import { AcqPicker } from "./components/AcqPicker";
import { BatchUnlockBar } from "./components/BatchUnlockBar";

const CAPITAL_OPTIONS = [
  { value: "100", label: "100 万以上" },
  { value: "500", label: "500 万以上" },
  { value: "1000", label: "1000 万以上" },
  { value: "5000", label: "5000 万以上" },
];
const YEARS_OPTIONS = [
  { value: "1", label: "1 年以上" },
  { value: "3", label: "3 年以上" },
  { value: "5", label: "5 年以上" },
  { value: "10", label: "10 年以上" },
];
const PRODUCT_KEY = "acq.ent.product";

function readProduct(): string {
  try {
    return localStorage.getItem(PRODUCT_KEY) ?? "";
  } catch {
    return "";
  }
}

function saveProduct(v: string) {
  try {
    localStorage.setItem(PRODUCT_KEY, v);
  } catch {
    /* 隐私模式下写不进去不影响功能 */
  }
}

/**
 * 企业库找客户(1015 企业库 P2;设计稿 v2「企业库找客户页」)。
 *
 * 左边选条件(行业 / 地区按客户画像预填),右边看结果勾选,底栏一键入线索库(免费);入库后可以接着批量补全联系方式。
 * ★ 价格先亮出来:顶栏常显每页价与今日余量;按钮写「按页计费」。0 条、24 小时内重看同一页都不扣。
 * ★ 和地图获客平级的独立页(目录里只放一块牌子指过来),线索写进主线索库 —— 不像地图获客那样另存一张表。
 */
export default function CompanyFinderPage() {
  const [options, setOptions] = useState<AcqEntOptions | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [cityGroups, setCityGroups] = useState<CityGroup[] | null | undefined>(undefined);
  const { products } = useTenantProducts();
  const persona = usePersona();
  const { book } = usePriceBook();

  const [section, setSection] = useState("");
  const [industry, setIndustry] = useState("");
  const [area, setArea] = useState<AreaPick | null>(null);
  const [keyword, setKeyword] = useState("");
  const [tag, setTag] = useState("");
  const [capitalMin, setCapitalMin] = useState("");
  const [yearsMin, setYearsMin] = useState("");
  const [product, setProduct] = useState(readProduct);
  const [fromProfile, setFromProfile] = useState({ industry: false, area: false, keyword: false });

  const [result, setResult] = useState<AcqEntSearchResult | null>(null);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [importing, setImporting] = useState(false);
  const [imported, setImported] = useState<string[]>([]);

  const [suggestions, setSuggestions] = useState<AcqEntSuggestion[]>([]);
  const suggestTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let alive = true;
    acqService
      .entOptions()
      .then((o) => alive && setOptions(o))
      .catch((e) => alive && setLoadError(errorText(e, { fallbackTitle: "企业库加载失败" })));
    listCityGroups()
      .then((g) => alive && setCityGroups(g))
      .catch(() => alive && setCityGroups(null));
    return () => {
      alive = false;
    };
  }, []);

  // 画像预填:行业取第一个对上的大类;地区按名字对行政区(城市优先,其次省);关键词取第一个。只预填一次。
  const prefilled = useRef(false);
  useEffect(() => {
    if (!options || cityGroups === undefined || prefilled.current) return;
    prefilled.current = true;
    const code = options.prefill.industryCodes[0];
    if (code) {
      const sec = options.industries.find((s) => s.items.some((i) => i.code === code));
      if (sec) {
        setSection(sec.code);
        setIndustry(code);
        setFromProfile((f) => ({ ...f, industry: true }));
      }
    }
    const kw = options.prefill.keywords?.[0];
    if (kw) {
      setKeyword(kw);
      setFromProfile((f) => ({ ...f, keyword: true }));
    }
    for (const name of options.prefill.regionNames) {
      const hit = matchRegion(name, cityGroups ?? null);
      if (hit) {
        setArea(hit);
        setFromProfile((f) => ({ ...f, area: true }));
        break;
      }
    }
  }, [options, cityGroups]);

  const sectionItems = useMemo(
    () => options?.industries.find((s) => s.code === section)?.items ?? [],
    [options, section]
  );

  const unlockMin = book.unit(SKU.acqUnlock);
  const unlockMax = book.unit(SKU.acqUnlockCompany);
  const price = options?.priceCredits ?? null;
  const priceText = price == null ? "按页计费" : `${price} 积分/页`;
  const remaining = options ? Math.max(0, options.dailyLimit - options.usedToday) : null;
  const hasCondition = !!(industry || area || keyword.trim() || tag);
  const tagNeedsScope = !!tag && !industry && !area;
  // 1020 真调:天眼查不带关键词一律查不到(按企业名匹配),行业 / 地区只是缩小范围;标签搜不受此限
  const needsKeyword = !tag && !keyword.trim();

  const run = useCallback(
    async (page: number) => {
      setSearching(true);
      setSelected(new Set());
      setImported([]);
      try {
        const r = await acqService.entSearch({
          industryCode: industry || null,
          areaCode: area ? areaCodeOf(area.adcode) : null,
          keyword: keyword.trim() || null,
          tagName: tag || null,
          page,
          capitalMinWan: capitalMin ? Number(capitalMin) : null,
          estYearsMin: yearsMin ? Number(yearsMin) : null,
        });
        setResult(r);
        if (r.items.length === 0) {
          toast({
            title: "这一页没有结果",
            description: "没扣分。可以换个关键词、放宽地区、换上一级行业或去掉资本 / 年限条件。",
          });
        }
        setOptions((o) => (o && r.chargedCredits > 0 ? { ...o, usedToday: o.usedToday + 1 } : o));
      } catch (e) {
        toast(errorToast(e, { fallbackTitle: "搜索没成功，没扣分" }));
      } finally {
        setSearching(false);
      }
    },
    [industry, area, keyword, tag, capitalMin, yearsMin]
  );

  const onKeyword = (v: string) => {
    setKeyword(v);
    setFromProfile((f) => ({ ...f, keyword: false }));
    if (suggestTimer.current) clearTimeout(suggestTimer.current);
    if (!options?.suggestEnabled || v.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    suggestTimer.current = setTimeout(() => {
      acqService
        .entSuggest(v.trim())
        .then(setSuggestions)
        .catch(() => setSuggestions([]));
    }, 400);
  };

  const selectable = (result?.items ?? []).filter((c) => !c.inLeads && c.creditCode);
  const allChecked = selectable.length > 0 && selectable.every((c) => selected.has(c.creditCode!));

  const toggle = (c: AcqEntCompany, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(c.creditCode!);
      else next.delete(c.creditCode!);
      return next;
    });

  const doImport = async () => {
    if (!result) return;
    setImporting(true);
    try {
      const r = await acqService.entImport(result.searchId, [...selected], product || null);
      toast({
        title: `已加入线索库 ${r.created} 家`,
        description:
          (r.existed > 0
            ? `另有 ${r.existed} 家原本就在库里${
                r.heldByColleague ? `（其中 ${r.heldByColleague} 家在同事名下）` : ""
              }。`
            : "") + "入库免费；要电话可以接着点「批量补全联系方式」，查到才扣。",
      });
      setImported(r.opportunityIds);
      setResult((prev) =>
        prev
          ? {
              ...prev,
              items: prev.items.map((c) =>
                c.creditCode && selected.has(c.creditCode) ? { ...c, inLeads: true } : c
              ),
            }
          : prev
      );
      setSelected(new Set());
    } catch (e) {
      toast(errorToast(e, { fallbackTitle: "加入线索库没成功" }));
    } finally {
      setImporting(false);
    }
  };

  if (loadError) {
    return (
      <AcqShell subtitle="企业库找客户">
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {loadError}
        </div>
      </AcqShell>
    );
  }

  if (!options) {
    return (
      <AcqShell subtitle="企业库找客户">
        <Skeleton className="h-64 w-full rounded-xl" />
      </AcqShell>
    );
  }

  if (!options.searchEnabled) {
    return (
      <AcqShell subtitle="企业库找客户">
        <div className="rounded-xl border border-dashed bg-card px-6 py-12 text-center">
          <Building2 className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm font-semibold">企业库找客户暂未开通</p>
          <p className="mt-1 text-xs text-muted-foreground">
            开通后可以按企业名关键词找工商登记的公司,再用行业、地区、标签缩小范围。线索卡上的「补全联系方式」不受影响。
          </p>
        </div>
      </AcqShell>
    );
  }

  const total = result?.total ?? null;
  const page = result?.page ?? 1;

  return (
    <AcqShell
      subtitle="按企业名关键词找工商登记的公司，行业、地区、标签缩小范围，勾选后进线索库"
      actions={
        <span className="text-2xs text-muted-foreground" data-testid="ent-price-hint">
          {`每页 ${price ?? "?"} 积分 · 今日还能搜 ${remaining ?? "?"} 页 · 0 条不扣 · 24 小时内重看同一页不扣`}
        </span>
      }
    >
      <div className="grid gap-4 lg:grid-cols-[18rem_1fr]">
        {/* ── 左:条件 ─────────────────────────────────────────────── */}
        <section className="space-y-3 rounded-xl border bg-card p-4" aria-label="筛选条件">
          <div className="space-y-1.5">
            <p className="text-2xs text-muted-foreground">
              行业(国标){fromProfile.industry && <Tag>来自客户画像</Tag>}
            </p>
            <AcqPicker
              label="行业门类"
              value={section}
              onChange={(v) => {
                setSection(v);
                setIndustry("");
                setFromProfile((f) => ({ ...f, industry: false }));
              }}
              options={options.industries.map((s) => ({ value: s.code, label: s.name }))}
              className="w-full"
            />
            {section && (
              <AcqPicker
                label="行业大类"
                value={industry}
                onChange={(v) => {
                  setIndustry(v);
                  setFromProfile((f) => ({ ...f, industry: false }));
                }}
                options={sectionItems.map((i) => ({ value: i.code, label: i.name }))}
                className="w-full"
              />
            )}
          </div>

          <div className="space-y-1.5">
            <p className="text-2xs text-muted-foreground">
              地区{fromProfile.area && <Tag>来自客户画像</Tag>}
            </p>
            {cityGroups ? (
              <CityCombobox
                cityGroups={cityGroups}
                selCityAdcode={area?.adcode ?? ""}
                onSelectCity={(adcode) => {
                  const hit = cityGroups.flatMap((g) => g.cities).find((c) => c.adcode === adcode);
                  setArea(hit ? { adcode: hit.adcode, name: hit.name } : null);
                  setFromProfile((f) => ({ ...f, area: false }));
                }}
              />
            ) : (
              <AcqPicker
                label="省份"
                value={area?.adcode ?? ""}
                onChange={(v) => {
                  const p = PROVINCES.find((x) => x.adcode === v);
                  setArea(p ? { adcode: p.adcode, name: p.name } : null);
                  setFromProfile((f) => ({ ...f, area: false }));
                }}
                options={PROVINCES.map((p) => ({ value: p.adcode, label: p.name }))}
                className="w-full"
              />
            )}
          </div>

          <div className="relative space-y-1.5">
            <p className="text-2xs text-muted-foreground">
              关键词(必填){fromProfile.keyword && <Tag>来自客户画像</Tag>}
            </p>
            <Input
              value={keyword}
              placeholder="例：烘焙、冷链"
              onChange={(e) => onKeyword(e.target.value)}
              onBlur={() => setTimeout(() => setSuggestions([]), 150)}
              aria-label="关键词"
            />
            {suggestions.length > 0 && (
              <ul className="absolute z-10 mt-1 w-full overflow-hidden rounded-md border bg-popover text-sm shadow">
                {suggestions.map((s) => (
                  <li key={s.creditCode ?? s.name}>
                    <button
                      type="button"
                      className="w-full px-3 py-1.5 text-left hover:bg-muted"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setKeyword(s.name);
                        setFromProfile((f) => ({ ...f, keyword: false }));
                        setSuggestions([]);
                      }}
                    >
                      {s.name}
                      {s.regStatus && (
                        <span className="ml-2 text-2xs text-muted-foreground">{s.regStatus}</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {options.tags.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-2xs text-muted-foreground">标签(选填)</p>
              <AcqPicker
                label="标签"
                value={tag}
                onChange={setTag}
                options={options.tags.map((t) => ({ value: t, label: t }))}
                className="w-full"
              />
              {tagNeedsScope && (
                <p className="text-2xs text-destructive">按标签找时请再选一个行业或地区</p>
              )}
            </div>
          )}

          <div className="space-y-2">
            <AcqPicker
              label="注册资本"
              value={capitalMin}
              onChange={setCapitalMin}
              options={CAPITAL_OPTIONS}
              className="w-full"
            />
            <AcqPicker
              label="成立年限"
              value={yearsMin}
              onChange={setYearsMin}
              options={YEARS_OPTIONS}
              className="w-full"
            />
          </div>
          <p className="text-2xs text-muted-foreground">
            注册资本、成立年限只在当页结果里再过滤(企业库不支持按这两个筛),所以一页可能少于 20 家。
          </p>

          <div className="space-y-1.5">
            <p className="text-2xs text-muted-foreground">{`主推产品(${persona.title}写话术围绕它)`}</p>
            <AcqPicker
              label="主推产品"
              value={product}
              onChange={(v) => {
                setProduct(v);
                saveProduct(v);
              }}
              options={products.map((p) => ({ value: p.nodeCode, label: p.name }))}
              emptyHint="还没有产品"
              className="w-full"
            />
          </div>

          {options.prefill.exclusions.length > 0 && (
            <p className="text-2xs text-muted-foreground">
              客户画像里的排除项:{options.prefill.exclusions.join("、")}。勾选时留意别把同行加进来。
            </p>
          )}

          <Button
            type="button"
            className="w-full"
            disabled={!hasCondition || tagNeedsScope || needsKeyword || searching}
            onClick={() => void run(1)}
          >
            <Search className="h-4 w-4" />
            {`搜索(${priceText})`}
          </Button>
          {(!hasCondition || needsKeyword) && (
            <p className="text-2xs text-muted-foreground">
              先填关键词:企业库按企业名称匹配,只选行业、地区查不到,它们用来缩小范围。
              <Link to="/ai-huoke/discover" className="ml-1 text-primary hover:underline">
                先去确认客户画像
              </Link>
              ,条件会自动填好。
            </p>
          )}
        </section>

        {/* ── 右:结果 ─────────────────────────────────────────────── */}
        <section className="min-w-0 space-y-3">
          {searching && <Skeleton className="h-64 w-full rounded-xl" />}

          {!searching && !result && (
            <div className="rounded-xl border border-dashed bg-card px-6 py-12 text-center text-sm text-muted-foreground">
              选好条件后点「搜索」。每页 20 家,入线索库免费,电话在入库后按条补全(查到才扣)。
            </div>
          )}

          {!searching && result && (
            <>
              <p className="text-2xs text-muted-foreground" data-testid="ent-result-head">
                {`第 ${page} 页 · ${result.items.length} 家`}
                {total != null ? ` · 共约 ${total} 家` : ""}
                {result.filteredOut > 0
                  ? ` · 已滤掉注销/吊销与不符合资本、年限的 ${result.filteredOut} 家`
                  : ""}
                {result.chargedCredits > 0
                  ? ` · 本页扣 ${result.chargedCredits} 积分`
                  : " · 本页未扣分"}
              </p>
              {result.items.length > 0 && (
                <div className="rounded-xl border bg-card">
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent">
                        <TableHead className="w-8">
                          <Checkbox
                            aria-label="全选本页可加入的公司"
                            checked={allChecked}
                            disabled={selectable.length === 0}
                            onCheckedChange={(v) =>
                              setSelected(
                                v === true
                                  ? new Set(selectable.map((c) => c.creditCode!))
                                  : new Set()
                              )
                            }
                          />
                        </TableHead>
                        <TableHead className="min-w-[14rem]">公司</TableHead>
                        <TableHead>法人</TableHead>
                        <TableHead>注册资本</TableHead>
                        <TableHead>成立</TableHead>
                        <TableHead>状态</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {result.items.map((c) => (
                        <TableRow
                          key={c.creditCode ?? c.name}
                          data-state={c.inLeads ? "disabled" : undefined}
                        >
                          <TableCell className="w-8">
                            {!c.inLeads && c.creditCode && (
                              <Checkbox
                                aria-label={`选择 ${c.name}`}
                                checked={selected.has(c.creditCode)}
                                onCheckedChange={(v) => toggle(c, v === true)}
                              />
                            )}
                          </TableCell>
                          <TableCell className="text-sm font-semibold">
                            {c.name}
                            {c.inLeads && (
                              <span className="ml-2 rounded-full bg-muted px-1.5 py-px text-2xs font-normal text-muted-foreground">
                                已在线索库
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs">{c.legalPerson ?? "—"}</TableCell>
                          <TableCell className="text-xs">{c.regCapital ?? "—"}</TableCell>
                          <TableCell className="text-xs">{c.establishDate ?? "—"}</TableCell>
                          <TableCell className="text-xs">{c.regStatus ?? "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              <div className="flex items-center justify-end gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={page <= 1}
                  onClick={() => void run(page - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                  上一页
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={result.items.length === 0 || page >= 50}
                  onClick={() => void run(page + 1)}
                >
                  下一页(再扣一页)
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </>
          )}

          {selected.size > 0 && (
            <div
              className="flex flex-wrap items-center gap-2 rounded-xl border bg-card px-3 py-2"
              data-testid="ent-import-bar"
            >
              <span className="text-sm">{`已选 ${selected.size} 家`}</span>
              <Button type="button" size="sm" disabled={importing} onClick={() => void doImport()}>
                加入线索库(免费)
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setSelected(new Set())}
              >
                取消选择
              </Button>
            </div>
          )}

          {imported.length > 0 && (
            <BatchUnlockBar
              selectedIds={imported}
              priceMin={unlockMin}
              priceMax={unlockMax}
              onClear={() => setImported([])}
              onDone={() => setImported([])}
            />
          )}
        </section>
      </div>
    </AcqShell>
  );
}

function Tag({ children }: { children: string }) {
  return (
    <span className="ml-1.5 rounded-full bg-primary/10 px-1.5 py-px text-2xs font-semibold text-primary">
      {children}
    </span>
  );
}
